import {Component, OnDestroy} from '@angular/core';
import {FormGroup, Validators} from '@angular/forms';
import {
    FieldUiDataObj,
    MxDynamicFormService,
    MxFormCustomizerEvent,
    MxFormDeactivation,
    MxFormErrorMessageService,
    MxNotificationService,
    MxPreloaderService,
    OnBoFormBeforeSubmit,
    OnBoFormReady,
    UiDataObj,
    withPreloader
} from 'mx-core';
import {combineLatestAll, Observable, of, Subject} from 'rxjs';
import {filter, finalize, take, takeUntil} from 'rxjs/operators';
import {PrlApplicationPropertiesDataService} from '../../../../shared/services/prl-application-properties.service';
import {PrlCaptchaFieldService} from '../../../../shared/services/prl-captcha-field.service';
import {PrlValidationManagerService} from '../../../../shared/services/prl-validation-manager.service';
import {PrlAffiliatesDataService} from '../../services/affiliate/prl-affiliates-data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PrlLanguageService} from '../../../prl-language/services/prl-language.service';

@Component({
    selector: 'prl-registration-page',
    templateUrl: './prl-registration-page.component.html',
    standalone: false,
    styleUrls: ['./prl-registration-page.component.scss']
})

export class PrlRegistrationPageComponent implements OnDestroy, OnBoFormReady, OnBoFormBeforeSubmit, MxFormDeactivation {

    closeConfirmation: () => Observable<boolean>;

    private _destroy$$: Subject<null> = new Subject<null>();

    private showDetailedSignup: boolean;
    private showSendPromotions: boolean;
    private vatPayerEnabled: boolean;
    private accessByProduct: boolean;
    private defaultLanguage: string;
    private defaultCountry: string;
    private form: FormGroup = null;

    protected usingCaptcha: boolean;
    protected captchaKey: string;

    public disabledRegistration: boolean;

    constructor(private applicationPropertiesService: PrlApplicationPropertiesDataService,
                private captchaFieldService: PrlCaptchaFieldService,
                private affiliatesDataService: PrlAffiliatesDataService,
                private dynamicFormService: MxDynamicFormService,
                private errorService: MxFormErrorMessageService,
                private preloader: MxPreloaderService,
                private notificationService: MxNotificationService,
                private readonly router: Router,
                private readonly route: ActivatedRoute,
                private validationManagerService: PrlValidationManagerService,
                private prlLanguageService: PrlLanguageService)
    {
        validationManagerService.init().subscribe();
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    onCustomize(event: MxFormCustomizerEvent): void {
        event.fieldsData.next({
            objectMetaSource: null,
            initialData: {}
        });

        if (this.router.url.includes('external_registration')) {
            event.customizeProps({showNavigation: false});
        }

        event
            .customizeFormMode('new')
            .customizeActions({submit: {text: 'label.submit', visible: true, disableFunction: this.disableButtons},
                cancel: {text: 'label.cancel', visible: true}})
            .customizeProps({
                addPrefixToTitle: false,
                notifyOnSaveFn: (saveOpWithPipe$, _action) => {
                    this.preloader.show({name: 'registration', type: 'RoundSpinner'});
                    return this.notificationService.notify(
                        saveOpWithPipe$.pipe(finalize(() => this.preloader.hide({name: 'registration'}))),
                        this.prlLanguageService.getLabel('label.signup.success.message'),
                        ''
                    ).pipe(
                        take(1),
                        filter(value => value === null || !value.error)
                    );
                }
            })
            .customizeFieldConfigs('productOfInterestIds', {style: `triple-width-column`})
            .customizeFieldConfigs('productIds', {style: `triple-width-column`})
            .customizeFieldConfigs('recaptcha', {style: `triple-width-column`});

        of(
            this.dynamicFormService.getUIData('com.playtech.mexos.contract.affiliate.PortalNewAffiliateObj', false, true),
            this.applicationPropertiesService.isShowDetailedSignup(),
            this.applicationPropertiesService.isEnableVatPayer(),
            this.applicationPropertiesService.isAccessByProduct(),
            this.applicationPropertiesService.isRegistrationDisabled(),
            this.applicationPropertiesService.showSendPromotions(),
            this.captchaFieldService.isUsingCaptcha(),
            this.captchaFieldService.getCaptchaKey(),
            this.applicationPropertiesService.getDefaultLanguage(),
            this.applicationPropertiesService.getDefaultCountry()
        )
            .pipe(takeUntil(this._destroy$$), withPreloader(this.preloader), combineLatestAll())
            .subscribe((response) => {
                let fieldSource: UiDataObj = response[0] as UiDataObj;
                this.showDetailedSignup = Boolean(response[1]);
                this.vatPayerEnabled = Boolean(response[2]);
                this.accessByProduct = Boolean(response[3]);
                this.disabledRegistration = Boolean(response[4]);
                this.showSendPromotions = Boolean(response[5]);
                this.usingCaptcha = Boolean(response[6]);
                if (this.usingCaptcha) {
                    this.captchaKey = response[7] as string;
                }
                this.defaultLanguage = response[8] as string;
                this.defaultCountry = response[9] as string;

                fieldSource.fieldsData.forEach(value => {
                    if (value.name === 'address1' || value.name === 'address2') {
                        value.hidden = !this.showDetailedSignup;
                    }
                    if (value.name === 'companyId') {
                        value.hidden = !this.showDetailedSignup;
                    }
                    if (value.name === 'countryCode') {
                        value.defaultValue = this.defaultCountry;
                    }
                    if (value.name === 'languageCode') {
                        value.defaultValue = this.defaultLanguage;
                    }
                    if (value.name === 'password') {
                        value['retypeLabel'] = 'label.confirm.password';
                    }
                    if (value.name === 'productIds' && this.accessByProduct) {
                        value['selectionMode'] = 'multi';
                        value.validation.required = true;
                        this.removeFieldByName(fieldSource.fieldsData, 'productOfInterestIds');
                    }
                    if (value.name === 'productOfInterestIds' && !this.accessByProduct) {
                        value['selectionMode'] = 'multi';
                        value.validation.required = true;
                        this.removeFieldByName(fieldSource.fieldsData, 'productIds');
                    }
                    if (value.name === 'sendPromotions') {
                        value.hidden = !this.showSendPromotions;
                        if (this.usingCaptcha) {
                            this.captchaFieldService.injectCaptchaField(fieldSource.fieldsData, value);
                        }
                    }
                    if (value.name === 'majorTermsAndConditionAccepted') {
                        value.validation.required = true;
                        value['unshielded'] = true;
                    }
                    if (value.name === 'vatPayer') {
                        value.hidden = !this.vatPayerEnabled;
                    }
                });
                event.fieldsData.next({
                    objectMetaSource: fieldSource,
                    initialData: {referrer: this.readReferrer()}
                });
            });
        this.closeConfirmation = event.closeConfirmationFunction;
        event.addCustomizer(this);
    }

    boOnFormReady(form: FormGroup): void {
        // it is the way to add unique name validator
        form.setAsyncValidators(this.errorService.asyncValidator(this.affiliatesDataService));
        this.form = form;

        this.updateTermsAndConditions(form);
        this.updateCompanyIdVisibility(form);
        this.updateStateVisibility(form);
        this.updateTaxIdVisibility(form);
    }

    boOnBeforeSubmit(form: FormGroup): void {
        let array = [form.controls.address1, form.controls.address2];
        array
            .filter(field => field.value === null)
            .map(field => field.setValue(''));
    }

    resolved(event: string) {
        this.captchaFieldService.resolve(this.form, event);
    }

    protected get usernameTooltip(): string {
        return this.prlLanguageService.getLabel('label.signup.username.tooltip');
    }

    private disableButtons = (): boolean => {
        if (!this.form) {
            return false;
        }

        return !this.form.touched;
    };

    private readReferrer(): string | null {
        return this.route.snapshot.queryParamMap.get('referrer');
    }

    private removeFieldByName = (array: FieldUiDataObj[], name: string) => {
        array
            .filter(field => field.name === name)
            .forEach(field => {
                let index = array.indexOf(field);
                array.splice(index, 1);
            });
    };

    private updateTermsAndConditions = (form: FormGroup) => {
        form.controls.majorTermsAndConditionAccepted.addValidators(Validators.requiredTrue)
    };

    private updateCompanyIdVisibility = (form: FormGroup) => {
        if (!this.showDetailedSignup) {
            return;
        }
        let address1 = form.controls.address1;
        let address2 = form.controls.address2;
        let company = form.controls.company;
        let companyId = form.controls.companyId;
        companyId.clearValidators();
        companyId.disable();
        address1.disable();
        address2.disable();
        companyId.updateValueAndValidity();
        company.valueChanges.pipe(
            takeUntil(this._destroy$$)
        ).subscribe(value => {
            if (!value || value === '') {
                address1.disable();
                address1.setValue(null);
                address1.clearValidators();
                address2.disable();
                address2.setValue(null);
                companyId.disable();
                companyId.setValue(null);
                companyId.clearValidators();
            } else {
                address1.enable();
                address1.addValidators([Validators.required]);
                address2.enable();
                companyId.enable();
                companyId.setValidators([Validators.required, Validators.minLength(5),
                    Validators.maxLength(15), this.errorService.boPatternValidator('DIGITS_ONLY')]);
            }
            address1.updateValueAndValidity();
            address2.updateValueAndValidity();
            companyId.updateValueAndValidity();
        });
    };

    private updateStateVisibility = (form: FormGroup) => {
        let countryCode = form.controls.countryCode;
        let state = form.controls.state;
        state.disable();
        countryCode.valueChanges.pipe(
            takeUntil(this._destroy$$)
        ).subscribe(value => {
            if (value !== 'US') {
                state.disable();
                state.setValue(null);
                state.clearValidators();
            } else {
                state.enable();
                state.addValidators([Validators.required, Validators.maxLength(2), Validators.minLength(2)]);
            }
            state.updateValueAndValidity();
        });
    };

    private updateTaxIdVisibility = (form: FormGroup) => {
        let taxId = form.controls.taxId;
        let country = form.controls.countryCode;
        taxId.disable();
        taxId.setValue(null);
        taxId.clearValidators();
        if (!this.vatPayerEnabled) {
            return;
        }

        let vatPayer = form.controls.vatPayer;
        vatPayer.valueChanges.pipe(
            takeUntil(this._destroy$$)
        ).subscribe(value => {
            if (value) {
                taxId.enable();
                taxId.addValidators([Validators.required, Validators.maxLength(17), this.errorService.boPatternValidator('TAX_ID')]);
                if (country.value) {
                    taxId.setValue(country.value);
                }
            } else {
                taxId.disable();
                taxId.setValue(null);
                taxId.clearValidators();
            }
            taxId.updateValueAndValidity();
        });

        country.valueChanges.pipe(
            takeUntil(this._destroy$$)
        ).subscribe(value => {
            if (value && vatPayer.value === true) {
                taxId.setValue(value);
            }
            taxId.updateValueAndValidity();
        });
    };
}
